class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    stopLenis();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    startLenis();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function () {
  const KM_COMPARE_AT_BY_VARIANT_ID = {
    50845796499759: 17800,  // 1 Month Supply
    51262651400495: 53400,  // 3 Month Supply
    51262730502447: 106800  // 6 Month Supply
  };

  const formatMoney = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  let kmCompareAtTimer = null;
  let kmCompareAtRequestInFlight = false;

  async function kmUpdateCartCompareAtPrices() {
    clearTimeout(kmCompareAtTimer);

    kmCompareAtTimer = setTimeout(async function () {
      const cartDrawerItems = document.querySelector('#CartDrawer-CartItems');
      if (!cartDrawerItems) return;

      if (kmCompareAtRequestInFlight) return;
      kmCompareAtRequestInFlight = true;

      let cart;

      try {
        cart = await fetch('/cart.js').then((response) => response.json());
      } catch (error) {
        console.warn('KM cart compare-at override failed:', error);
        return;
      } finally {
        kmCompareAtRequestInFlight = false;
      }

      const rows = [...document.querySelectorAll('#CartDrawer-CartItems tr.cart-item')];

      cart.items.forEach((item, index) => {
        const compareUnit = KM_COMPARE_AT_BY_VARIANT_ID[item.variant_id];
        if (!compareUnit) return;

        const row = rows[index];
        if (!row) return;

        const oldPrices = row.querySelectorAll('.cart-item__old-price');

        // Price under product title
        if (oldPrices[0]) {
          const formattedUnitPrice = formatMoney(compareUnit);
          if (oldPrices[0].textContent.trim() !== formattedUnitPrice) {
            oldPrices[0].textContent = formattedUnitPrice;
          }
        }

        // Right-side line total
        if (oldPrices[1]) {
          const formattedLinePrice = formatMoney(compareUnit * item.quantity);
          if (oldPrices[1].textContent.trim() !== formattedLinePrice) {
            oldPrices[1].textContent = formattedLinePrice;
          }
        }
      });
    }, 75);
  }

  document.addEventListener('DOMContentLoaded', kmUpdateCartCompareAtPrices);
  document.addEventListener('cart:updated', kmUpdateCartCompareAtPrices);

  const observer = new MutationObserver(kmUpdateCartCompareAtPrices);

  document.addEventListener('DOMContentLoaded', function () {
    const cartDrawer = document.querySelector('#CartDrawer');
    if (!cartDrawer) return;

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });

    kmUpdateCartCompareAtPrices();
  });

  const originalFetch = window.fetch;

  window.fetch = async function () {
    const response = await originalFetch.apply(this, arguments);

    try {
      const requestUrl = String(arguments[0]);

      if (
        requestUrl.includes('/cart/add') ||
        requestUrl.includes('/cart/change') ||
        requestUrl.includes('/cart/update')
      ) {
        setTimeout(kmUpdateCartCompareAtPrices, 150);
        setTimeout(kmUpdateCartCompareAtPrices, 500);
      }
    } catch (error) {}

    return response;
  };

  window.KM_updateCartCompareAtPrices = kmUpdateCartCompareAtPrices;
})();